import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppIcon from '../../../components/ui/AppIcon';
import { searchMessages } from '../../../services/chatServices';
import { useChat } from '../../../context/ChatContext';
import { useAuth } from '../../../context/AuthContext';
import HighlightedText from '../../../components/HighlightedText';

function useDebounce(value, delay = 400) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

export default function SearchOverlay({ onClose }) {
    const { selectChat, channels, directMessages, usersData, setTargetMessageId, setHighlightQuery } = useChat();
    const { user } = useAuth();
    const currentUserId = user?._id || user?.id;

    const [query, setQuery] = useState('');
    const [filterSenderId, setFilterSenderId] = useState('');
    const [filterChannelId, setFilterChannelId] = useState('');
    const [filterFromDate, setFilterFromDate] = useState('');
    const [filterToDate, setFilterToDate] = useState('');
    const [sortBy, setSortBy] = useState('latest');
    const [showFilters, setShowFilters] = useState(false);

    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [source, setSource] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const LIMIT = 10;
    const inputRef = useRef(null);
    const resultRefs = useRef([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const debouncedQuery = useDebounce(query, 400);
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (true) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, []);

    useEffect(() => {
        return () => {
            setQuery('');
            setResults([]);
            setTotal(0);
            setPage(1);
            setHasSearched(false);
            setShowFilters(false);
            setFilterSenderId('');
            setFilterChannelId('');
            setFilterFromDate('');
            setFilterToDate('');
            setSortBy('latest');
            setSelectedIndex(-1);
        };
    }, [onClose]);

    useEffect(() => {
        setSelectedIndex(-1);
        resultRefs.current = [];
    }, [results]);

    useEffect(() => {
        if (selectedIndex >= 0 && resultRefs.current[selectedIndex]) {
            resultRefs.current[selectedIndex].scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            });
        }
    }, [selectedIndex]);

    const handleResultClickRef = useRef(null);
    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case 'ArrowDown': {
                e.preventDefault();
                setSelectedIndex(prev =>
                    results.length === 0 ? -1 : Math.min(prev + 1, results.length - 1),
                );
                break;
            }
            case 'ArrowUp': {
                e.preventDefault();
                setSelectedIndex(prev => Math.max(prev - 1, -1));
                break;
            }
            case 'Enter': {
                if (selectedIndex >= 0 && results[selectedIndex]) {
                    e.preventDefault();
                    handleResultClickRef.current?.(results[selectedIndex]);
                }
                break;
            }
            case 'Escape': {
                onClose();
                break;
            }
            default:
                break;
        }
    }, [results, selectedIndex, onClose]);

    const doSearch = useCallback(async (pageNum = 1) => {
        const trimmedQ = debouncedQuery.trim();
        if (!trimmedQ && !filterSenderId && !filterChannelId && !filterFromDate && !filterToDate) {
            return;
        }

        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setHasSearched(true);

        const fromISO = filterFromDate ? `${filterFromDate}T00:00:00.000Z` : undefined;
        const toISO   = filterToDate   ? `${filterToDate}T23:59:59.999Z`   : undefined;

        try {
            const params = {
                ...(trimmedQ                    && { q: trimmedQ }),
                ...(filterSenderId              && { senderId:   String(filterSenderId).trim() }),
                ...(filterChannelId             && { channelId:  String(filterChannelId).trim() }),
                ...(fromISO                     && { fromDate:   fromISO }),
                ...(toISO                       && { toDate:     toISO }),
                sortBy,
                page: pageNum,
                limit: LIMIT,
            };

            const response = await searchMessages(params);

            if (requestId === requestIdRef.current) {
                if (response?.success) {
                    if (pageNum === 1) {
                        setResults(response.results || []);
                    } else {
                        setResults((prev) => [...prev, ...(response.results || [])]);
                    }
                    setTotal(response.total || 0);
                    setSource(response.source || '');
                }
            }
        } catch (error) {
            console.error('Search error:', error);
            if (requestId === requestIdRef.current) {
                setResults([]);
                setTotal(0);
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [debouncedQuery, filterSenderId, filterChannelId, filterFromDate, filterToDate, sortBy]);

    useEffect(() => {
        const trimmedQ = debouncedQuery.trim();
        const hasAnyFilter = trimmedQ || filterSenderId || filterChannelId || filterFromDate || filterToDate;

        if (hasAnyFilter) {
            setPage(1);
            doSearch(1);
        } else {
            setResults([]);
            setTotal(0);
            setHasSearched(false);
        }
    }, [debouncedQuery, filterSenderId, filterChannelId, filterFromDate, filterToDate, sortBy, doSearch]);

    const loadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        doSearch(nextPage);
    };

    const hasMore = results.length < total;

    const handleResultClick = useCallback((result) => {
        if (!result) return;

        const msgId = result.messageId || result._id;
        if (msgId) {
            setTargetMessageId(msgId);
        }
        if (query.trim()) {
            setHighlightQuery(query.trim());
        }

        if (result.channelId) {
            const target = channels.find(
                c => String(c._id || c.id) === String(result.channelId)
            );
            if (target) selectChat('channel', target, true);
        } else {
            let targetUserId;

            if (result.chatUniqueId) {
                targetUserId = result.chatUniqueId
                    .split('_')
                    .find(id => id !== String(currentUserId));
            }

            if (!targetUserId) {
                if (result.senderId && String(result.senderId) === String(currentUserId)) {
                    targetUserId = result.receiverId;
                } else if (result.receiverId && String(result.receiverId) === String(currentUserId)) {
                    targetUserId = result.senderId;
                } else {
                    targetUserId = result.receiverId || result.senderId;
                }
            }

            let target = directMessages.find(
                dm => String(dm._id || dm.id) === String(targetUserId)
            );

            if (!target) {
                target = usersData.find(
                    u => String(u._id || u.id) === String(targetUserId)
                );
            }

            if (!target && targetUserId) {
                const otherIsSender = String(result.senderId) !== String(currentUserId);
                const nameFromResult = otherIsSender
                    ? (typeof result.sender === 'string' ? result.sender : result.sender?.name)
                    : (typeof result.receiver === 'string' ? result.receiver : result.receiver?.name);

                target = {
                    _id: targetUserId,
                    id: targetUserId,
                    name: nameFromResult || 'Unknown',
                };
            }

            if (target) {
                selectChat('dm', target, true);
            }
        }
        onClose();
    }, [channels, directMessages, usersData, selectChat, onClose, setTargetMessageId, setHighlightQuery, query, currentUserId]);

    handleResultClickRef.current = handleResultClick;

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return (
            date.toLocaleDateString([], {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            }) +
            ' ' +
            date.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
            })
        );
    };

    const isChannelResult = (result) => !!result.channelId;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-xl p-4 animate-fade-in">
            <div
                className="w-full min-w-0 max-w-3xl max-h-[88vh] flex flex-col bg-app-surface rounded-3xl shadow-2xl overflow-hidden animate-fly-in"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-20 -right-20 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-secondary/10 rounded-full blur-3xl" />
                </div>

                <div className="relative flex flex-col min-h-0 flex-1">
                    <div className="flex items-center gap-2 sm:gap-4 px-4 sm:px-6 py-4 sm:py-5 border-b border-border-subtle bg-app-bg/20">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-xl opacity-20 blur-lg" />
                            <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg">
                                <AppIcon name="MagnifyingGlassIcon" size={18} className="text-white" />
                            </div>
                        </div>
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search messages..."
                            className="flex-1 min-w-0 bg-transparent text-base text-text-primary placeholder:text-text-muted/50 outline-none"
                        />
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`p-2.5 rounded-xl transition-all ${
                                showFilters
                                    ? 'bg-primary text-white shadow-lg shadow-primary/20'
                                    : 'text-text-muted hover:text-text-primary hover:bg-black/5'
                            }`}
                            title="Filters"
                        >
                            <AppIcon name="FunnelIcon" size={18} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2.5 rounded-xl text-text-muted hover:text-text-primary hover:bg-black/5 transition-all"
                        >
                            <AppIcon name="XMarkIcon" size={20} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="px-6 py-6 border-b border-border-subtle bg-app-bg/30 animate-slide-down">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2.5 block flex items-center gap-2">
                                        <AppIcon name="UserIcon" size={12} className="text-primary" />
                                        Sender
                                    </label>
                                    <select
                                        value={filterSenderId}
                                        onChange={(e) => setFilterSenderId(e.target.value)}
                                        className="w-full text-sm border border-border-subtle rounded-2xl px-4 py-3 bg-app-surface text-text-primary outline-none focus:border-primary/50 appearance-none cursor-pointer shadow-subtle focus:ring-4 focus:ring-primary/5"
                                    >
                                        <option value="" className="bg-app-surface text-text-primary">All Senders</option>
                                        {usersData?.map((user) => (
                                            <option key={user._id} value={user._id} className="bg-app-surface text-text-primary">
                                                {user.name || user.email || 'Unknown'}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2.5 block flex items-center gap-2">
                                        <AppIcon name="HashtagIcon" size={12} className="text-primary" />
                                        Channel
                                    </label>
                                    <select
                                        value={filterChannelId}
                                        onChange={(e) => setFilterChannelId(e.target.value)}
                                        className="w-full text-sm border border-border-subtle rounded-2xl px-4 py-3 bg-app-surface text-text-primary outline-none focus:border-primary/50 appearance-none cursor-pointer shadow-subtle focus:ring-4 focus:ring-primary/5"
                                    >
                                        <option value="" className="bg-app-surface text-text-primary">All Channels + DMs</option>
                                        {channels?.map((ch) => (
                                            <option key={ch._id || ch.id} value={ch._id || ch.id} className="bg-app-surface text-text-primary">
                                                # {ch.name || 'Unnamed'}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2.5 block">
                                        From Date
                                    </label>
                                    <input
                                        type="date"
                                        value={filterFromDate}
                                        onChange={(e) => setFilterFromDate(e.target.value)}
                                        className="w-full text-sm border border-border-subtle rounded-2xl px-4 py-3 bg-app-surface text-text-primary outline-none focus:border-primary/50 appearance-none shadow-subtle focus:ring-4 focus:ring-primary/5"
                                    />
                                </div>

                                <div>
                                    <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2.5 block">
                                        To Date
                                    </label>
                                    <input
                                        type="date"
                                        value={filterToDate}
                                        onChange={(e) => setFilterToDate(e.target.value)}
                                        className="w-full text-sm border border-border-subtle rounded-2xl px-4 py-3 bg-app-surface text-text-primary outline-none focus:border-primary/50 appearance-none shadow-subtle focus:ring-4 focus:ring-primary/5"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between mt-5">
                                <div className="flex items-center gap-3">
                                    <AppIcon name="ClockIcon" size={14} className="text-text-muted" />
                                    <label className="text-xs font-medium text-text-muted">Sort:</label>
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="text-sm font-medium border border-border-subtle rounded-xl px-3 py-2 bg-app-surface text-text-primary outline-none focus:border-primary/50 appearance-none cursor-pointer shadow-subtle"
                                    >
                                        <option value="latest" className="bg-app-surface text-text-primary">Latest First</option>
                                        <option value="relevance" className="bg-app-surface text-text-primary">Most Relevant</option>
                                    </select>
                                </div>
                                <button
                                    onClick={() => {
                                        setFilterSenderId('');
                                        setFilterChannelId('');
                                        setFilterFromDate('');
                                        setFilterToDate('');
                                        setSortBy('latest');
                                    }}
                                    className="text-sm font-medium text-primary hover:bg-primary/10 px-4 py-2 rounded-xl transition-all"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    )}

                    {hasSearched && (
                        <div className="px-6 py-3 flex items-center justify-between border-b border-border-subtle bg-app-bg/10">
                            <span className="text-sm font-medium text-text-muted">
                                {isLoading ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                        Searching...
                                    </span>
                                ) : (
                                    `${total} result${total !== 1 ? 's' : ''} found`
                                )}
                            </span>
                            {source && (
                                <span className={`text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1.5 ${
                                    source === 'elasticsearch'
                                        ? 'bg-success/10 text-success border border-success/20'
                                        : 'bg-warning/10 text-warning border border-warning/20'
                                }`}>
                                    {source === 'elasticsearch' ? (
                                        <>
                                            <AppIcon name="BoltIcon" size={12} />
                                            ElasticSearch
                                        </>
                                    ) : (
                                        <>
                                            <AppIcon name="ArrowPathIcon" size={12} />
                                            MongoDB
                                        </>
                                    )}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-5 space-y-3 max-h-[60vh] sm:max-h-[450px]">
                        {isLoading && results.length === 0 && (
                            <div className="flex justify-center items-center py-16">
                                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                        )}

                        {!isLoading && hasSearched && results.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                                    <AppIcon name="MagnifyingGlassIcon" size={28} className="text-primary/50" />
                                </div>
                                <p className="text-sm font-semibold text-text-primary mb-1">No messages found</p>
                                <p className="text-xs text-text-muted">Try a different keyword or adjust filters</p>
                            </div>
                        )}

                        {!hasSearched && !isLoading && (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4">
                                    <AppIcon name="MagnifyingGlassIcon" size={28} className="text-primary" />
                                </div>
                                <p className="text-sm font-semibold text-text-primary mb-2">Type to search</p>
                                <p className="text-xs text-text-muted text-center max-w-xs">Search across all messages, channels, and conversations</p>
                            </div>
                        )}

                        {results.length > 0 && (
                            <div className="space-y-3">
                                {results.map((result, index) => {
                                    const isChannel = isChannelResult(result);
                                    const messageText = isChannel ? result.text : result.message;

                                    const highlightField = isChannel
                                        ? (result.highlight?.text || result.highlight?.senderName)
                                        : (result.highlight?.message || result.highlight?.sender || result.highlight?.receiver);

                                    const resolveName = (val) => {
                                        if (!val) return 'Unknown';
                                        if (typeof val === 'string') return val;
                                        return val.name || val.email || val.username || 'Unknown';
                                    };

                                    const senderName = isChannel
                                        ? result.senderName
                                        : resolveName(result.sender);
                                    const locationLabel = isChannel
                                        ? `# ${result.channelName || 'Channel'}`
                                        : `DM · ${resolveName(result.sender)} → ${resolveName(result.receiver)}`;

                                    const isSelected = index === selectedIndex;

                                    return (
                                        <div
                                            key={result.messageId || index}
                                            ref={(el) => (resultRefs.current[index] = el)}
                                            onClick={() => handleResultClick(result)}
                                            className={`group relative p-5 rounded-2xl cursor-pointer transition-all duration-200 border ${
                                                isSelected
                                                    ? 'bg-primary/10 border-primary/30 shadow-xl shadow-primary/5'
                                                    : 'bg-app-bg/50 border-border-subtle hover:bg-primary/5 hover:border-primary/20 hover:shadow-lg'
                                            }`}
                                            role="option"
                                            aria-selected={isSelected}
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="flex-shrink-0 mt-0.5">
                                                    {isChannel ? (
                                                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/20 shadow-md">
                                                            <AppIcon name="HashtagIcon" size={20} className="text-primary" />
                                                        </div>
                                                    ) : (
                                                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-secondary/20 to-secondary/5 flex items-center justify-center border border-secondary/20 shadow-md">
                                                            <AppIcon name="UserIcon" size={20} className="text-secondary" />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                        <span className="text-sm font-semibold text-text-primary">
                                                            {senderName}
                                                        </span>
                                                        <span className="text-xs text-text-muted">in</span>
                                                        <span className="text-xs font-semibold text-primary">
                                                            {locationLabel}
                                                        </span>
                                                        <span className="ml-auto text-xs text-text-muted flex-shrink-0">
                                                            {formatTime(result.timestamp)}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm text-text-secondary leading-relaxed line-clamp-2 [&_mark]:bg-primary/20 [&_mark]:text-primary [&_mark]:font-bold [&_mark]:px-1 [&_mark]:rounded">
                                                        <HighlightedText
                                                            text={messageText}
                                                            highlight={highlightField}
                                                            maxLength={120}
                                                        />
                                                    </div>
                                                </div>

                                                <div className={`flex-shrink-0 text-primary mt-1.5 transition-all ${
                                                    isSelected ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'
                                                }`}>
                                                    <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shadow-lg">
                                                        <AppIcon name="ArrowRightIcon" size={14} className="text-white" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {hasMore && (
                                    <div className="flex justify-center py-4">
                                        <button
                                            onClick={loadMore}
                                            disabled={isLoading}
                                            className="flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-medium text-primary hover:bg-primary/10 transition-all disabled:opacity-50 shadow-md hover:shadow-lg"
                                        >
                                            {isLoading ? (
                                                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <>
                                                    <AppIcon name="PlusIcon" size={16} />
                                                    Load more ({total - results.length} remaining)
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="hidden sm:flex px-6 py-4 bg-app-bg/20 border-t border-border-subtle items-center gap-4 text-xs font-medium text-text-muted">
                        <span className="flex items-center gap-2">
                            <kbd className="px-2.5 py-1.5 bg-app-surface rounded-lg border border-border-subtle text-text-primary font-semibold text-[10px]">↑↓</kbd>
                            Navigate
                        </span>
                        <span className="flex items-center gap-2">
                            <kbd className="px-2.5 py-1.5 bg-app-surface rounded-lg border border-border-subtle text-text-primary font-semibold text-[10px]">Enter</kbd>
                            Open
                        </span>
                        <span className="flex items-center gap-2">
                            <kbd className="px-2.5 py-1.5 bg-app-surface rounded-lg border border-border-subtle text-text-primary font-semibold text-[10px]">Esc</kbd>
                            Close
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}